<template>
  <section class="w-100 front-section">
    <div class="container-fluid hv-100">
      <div class="row p-md-4 pb-md-0 p-3 pt-5" style="overflow-x:hidden; padding-top: 4rem !important;">
        <div class="border-around-rounded p-3 pb-md-5 pb-3" style="margin-top: 60px; min-height: calc(100vh - 120px);">
          
          <!-- Standard Fixed Header -->
          <FrontHeader></FrontHeader>
          <div class="py-4 invisible">&nbsp;</div>

          <!-- Main Content Container -->
          <div class="container px-2 px-md-3">

            <!-- ===== HERO SECTION ===== -->
            <div class="text-center mb-5 mt-2">
              <div class="hero-badge mb-3 mx-auto">
                <span class="badge-text">✨ The Stella Story</span>
              </div>
              <h1 class="hero-title">
                Built for Creators,<br>
                <span class="hero-gradient-text">Owned by Creators</span>
              </h1>
              <p class="hero-subtitle mx-auto mt-3 mb-4">
                Stella was founded with an unyielding conviction: Creators should own their direct relationships with their audience, control their creative destiny, and keep the overwhelming majority of the wealth they generate.
              </p>
              <div class="d-flex flex-wrap gap-3 justify-content-center mt-4">
                <button class="btn stella-btn gradient-a px-4 py-2 rounded-pill font-family-poppins font-weight-600" @click="exploreCreators">
                  Explore Top Creators
                </button>
                <button class="btn btn-outline-light px-4 py-2 rounded-pill font-family-poppins font-weight-600" @click="startCreating">
                  Start Your Creator Page
                </button>
              </div>
            </div>

            <!-- ===== STATS STRIP ===== -->
            <div class="col-12 my-5 py-2">
              <div class="stats-strip custom-card-transparent">
                <div class="stat-item" v-for="(stat, i) in stats" :key="i">
                  <div class="stat-value">{{ stat.value }}</div>
                  <div class="stat-label">{{ stat.label }}</div>
                </div>
              </div>
            </div>

            <!-- ===== OUR MISSION / PROBLEM VS SOLUTION ===== -->
            <div class="col-12 my-5 py-3">
              <div class="text-center mb-5">
                <div class="hero-badge mb-3 mx-auto">
                  <span class="badge-text">🎯 Our Mission</span>
                </div>
                <h2 class="section-title text-white">Why We Built <span class="hero-gradient-text">Stella</span></h2>
                <p class="section-subtitle font-grey">The algorithmic social media model is broken for creators. We built the software infrastructure to replace it.</p>
              </div>

              <div class="row justify-content-center">
                <!-- Card 1: The Problem -->
                <div class="col-lg-6 col-12 mb-4">
                  <div class="custom-card-transparent comparison-card h-100 p-4 p-md-5">
                    <div class="d-flex align-items-center mb-3">
                      <span class="status-pill status-pill-warning me-2">⚠️ The Status Quo</span>
                    </div>
                    <h3 class="text-white font-weight-bold mb-3 font-family-poppins">The Algorithmic Trap</h3>
                    <p class="font-grey font-size-14 line-height-18 mb-4">
                      For over a decade, social monopolies built multi-billion dollar ad engines on creators' free labor. Creators face constant vulnerability:
                    </p>
                    <ul class="styled-list list-problem">
                      <li>
                        <strong>Unpredictable Algorithms:</strong> One tweak to an opaque feed ranking can wipe out 80% of your audience reach overnight.
                      </li>
                      <li>
                        <strong>Zero Audience Ownership:</strong> Platforms guard fan data jealously. You cannot message fans directly or export your audience.
                      </li>
                      <li>
                        <strong>Opaque Revenue Splits:</strong> Creators receive pennies on the dollar through fluctuating ad-revenue CPMs.
                      </li>
                      <li>
                        <strong>Creative Burnout:</strong> Trapped on a hamster wheel of pumping out daily short-form content just to stay relevant.
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Card 2: The Solution -->
                <div class="col-lg-6 col-12 mb-4">
                  <div class="custom-card-transparent comparison-card h-100 p-4 p-md-5 solution-glow">
                    <div class="d-flex align-items-center mb-3">
                      <span class="status-pill status-pill-success me-2">🚀 The Direct-to-Fan Era</span>
                    </div>
                    <h3 class="text-white font-weight-bold mb-3 font-family-poppins">Direct Creator Independence</h3>
                    <p class="font-grey font-size-14 line-height-18 mb-4">
                      Stella replaces algorithmic lottery tickets with reliable software infrastructure connecting you directly to your true community:
                    </p>
                    <ul class="styled-list list-solution">
                      <li>
                        <strong>Direct Fan Relationships:</strong> Zero algorithms between you and your subscribers. When you publish, every subscriber sees it.
                      </li>
                      <li>
                        <strong>Predictable Recurring Income:</strong> Build reliable monthly subscription revenue directly deposited into your bank account.
                      </li>
                      <li>
                        <strong>Total Data & Brand Ownership:</strong> You own your subscriber list, pricing, and custom domain presence from day one.
                      </li>
                      <li>
                        <strong>Focus on Deep Quality:</strong> Monetize high-value, bespoke interactions and content rather than chasing viral vanity metrics.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== WHAT WE BUILD (PRODUCT SUITE) ===== -->
            <div class="col-12 my-5 py-3">
              <div class="text-center mb-5">
                <div class="hero-badge mb-3 mx-auto">
                  <span class="badge-text">⚡ Platform Ecosystem</span>
                </div>
                <h2 class="section-title text-white">Monetization Built for <span class="hero-gradient-text">Modern Creators</span></h2>
                <p class="section-subtitle font-grey">Everything you need to run a high-margin digital creator business, unified in one seamless storefront.</p>
              </div>

              <div class="row justify-content-center">
                <div class="col-lg-3 col-md-6 col-12 mb-4" v-for="(tool, i) in tools" :key="i">
                  <div class="custom-card-transparent feature-pillar-card p-4 h-100 d-flex flex-column">
                    <div class="feature-pillar-icon mb-3">{{ tool.icon }}</div>
                    <h4 class="text-white font-weight-bold font-size-18 mb-2 font-family-poppins">{{ tool.title }}</h4>
                    <p class="font-grey font-size-13 mb-3 flex-grow-1 line-height-16">{{ tool.desc }}</p>
                    <div class="feature-pillar-highlight">{{ tool.highlight }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== THE MANIFESTO CALLOUT ===== -->
            <div class="col-12 my-5 py-2">
              <div class="custom-card-transparent manifesto-card text-center p-4 p-md-5">
                <div class="manifesto-quote-mark">“</div>
                <blockquote class="manifesto-text font-family-poppins">
                  You don’t need millions of passive followers to build a thriving creative career.<br class="d-none d-md-block">
                  You need <span class="hero-gradient-text font-weight-700">1,000 true fans</span> who genuinely value what you create.
                </blockquote>
                <div class="manifesto-author font-grey mt-4">
                  — The Stella Creator Manifesto
                </div>
              </div>
            </div>

            <!-- ===== CORE PRINCIPLES ===== -->
            <div class="col-12 my-5 py-3">
              <div class="text-center mb-5">
                <div class="hero-badge mb-3 mx-auto">
                  <span class="badge-text">💎 Guiding Principles</span>
                </div>
                <h2 class="section-title text-white">How We <span class="hero-gradient-text">Operate</span></h2>
                <p class="section-subtitle font-grey">Our core commitments to every creator building their business on our platform.</p>
              </div>

              <div class="row justify-content-center">
                <div class="col-lg-4 col-md-6 col-12 mb-4" v-for="(principle, i) in principles" :key="i">
                  <div class="custom-card-transparent p-4 h-100 d-flex flex-column">
                    <div class="principle-number mb-2">{{ principle.number }}</div>
                    <h4 class="text-white font-weight-bold font-size-18 mb-2 font-family-poppins">{{ principle.title }}</h4>
                    <p class="font-grey font-size-14 line-height-16 mb-0">{{ principle.desc }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== EDITORIAL & INSIGHTS ===== -->
            <div class="col-12 my-5 py-3">
              <div class="row align-items-center justify-content-between mb-4">
                <div class="col-md-8 col-12 text-start">
                  <div class="hero-badge mb-2">
                    <span class="badge-text">📚 Creator Playbooks</span>
                  </div>
                  <h3 class="section-title text-white font-size-28 mb-1">From the <span class="hero-gradient-text">Stella Editorial</span></h3>
                  <p class="section-subtitle font-grey mb-0">Practical tactical guides, monetization case studies, and creator economy insights.</p>
                </div>
                <div class="col-md-4 col-12 text-md-end text-start mt-3 mt-md-0">
                  <a href="https://blog.stella.so" target="_blank" rel="noopener" class="btn btn-outline-light rounded-pill px-4 py-2 font-family-poppins font-size-13">
                    Visit Stella Blog ↗
                  </a>
                </div>
              </div>

              <div class="row justify-content-center">
                <div class="col-lg-4 col-md-6 col-12 mb-4" v-for="(article, i) in articles" :key="i">
                  <div class="custom-card-transparent article-card p-4 h-100 d-flex flex-column" @click="openBlog(article.url)">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <span class="article-tag">{{ article.tag }}</span>
                      <span class="font-grey font-size-12">{{ article.readTime }}</span>
                    </div>
                    <h5 class="text-white font-weight-bold font-size-16 mb-2 font-family-poppins line-clamp-2">
                      {{ article.title }}
                    </h5>
                    <p class="font-grey font-size-13 line-height-16 mb-4 flex-grow-1 line-clamp-3">
                      {{ article.excerpt }}
                    </p>
                    <div class="d-flex align-items-center text-primary-gradient font-size-13 font-weight-600 mt-auto">
                      <span>Read Playbook</span>
                      <span class="ms-2">→</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== BOTTOM CTA BANNER ===== -->
            <div class="col-12 my-5 py-2">
              <div class="custom-card-transparent cta-card text-center p-4 p-md-5">
                <h2 class="cta-title mb-3">
                  Ready to Take Ownership of Your Creative Future?
                </h2>
                <p class="cta-subtitle mx-auto mb-4">
                  Join hundreds of forward-thinking creators who have escaped the algorithm trap and built sustainable direct-to-fan businesses.
                </p>
                <div class="d-flex flex-wrap gap-3 justify-content-center">
                  <button class="btn stella-btn gradient-a px-5 py-3 rounded-pill font-family-poppins font-weight-600 font-size-15" @click="exploreCreators">
                    Discover Top Creators
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    
    <!-- Standard Footer with Contact Details -->
    <FrontFooter></FrontFooter>
  </section>
</template>

<script>
import FrontHeader from '@/components/module/front/FrontHeader';
import FrontFooter from '@/components/module/front/FrontFooter';
import titleMixin from "@/mixin/title-mixin";

export default {
  name: 'AboutPage',
  title: 'About',
  mixins: [titleMixin],
  components: {
    FrontHeader,
    FrontFooter
  },
  data() {
    return {
      stats: [
        { value: '95%+', label: 'Creator Payout Rate' },
        { value: '1,000', label: 'True Fans Philosophy' },
        { value: '100%', label: 'Audience Data Ownership' },
        { value: '0', label: 'Algorithmic Middlemen' }
      ],
      tools: [
        {
          icon: '👑',
          title: 'Tiered Memberships',
          desc: 'Build predictable monthly recurring income with tiered subscriber perks, VIP badges, and exclusive media feeds.',
          highlight: 'Recurring Revenue'
        },
        {
          icon: '💬',
          title: 'Priority Direct Messaging',
          desc: 'Monetize direct access with custom pricing for 1-on-1 texts, paid audio voice notes, and personalized video replies.',
          highlight: 'Pay-Per-Message'
        },
        {
          icon: '🎨',
          title: 'Custom Content Requests',
          desc: 'Turnkey commission storefront for fan shoutouts, portfolio consultations, bespoke artwork, and custom video requests.',
          highlight: 'Bespoke Commissions'
        },
        {
          icon: '⚡',
          title: 'Digital Media Storefront',
          desc: 'Sell pay-per-view video releases, photo packs, presets, and digital downloads directly to fans with instant Stripe checkout.',
          highlight: 'Instant Payouts'
        }
      ],
      principles: [
        {
          number: '01',
          title: 'Creators First, Always',
          desc: 'We succeed only when our creators succeed. Transparent 20% platform fee, zero hidden charges, and dependable monthly payouts directly to your account.'
        },
        {
          number: '02',
          title: 'Total Audience Ownership',
          desc: 'Your fans belong to you. We never lock your audience in, restrict communications, or hide subscriber information. You own your creative relationships.'
        },
        {
          number: '03',
          title: 'Built for Sustainable Careers',
          desc: 'We optimize for sustainable lifetime creator revenue rather than chasing ephemeral viral reach. Depth of community will always beat passive follower counts.'
        }
      ],
      articles: [
        {
          tag: 'Playbook',
          readTime: '4 min read',
          title: 'The 1,000 True Fans Playbook for Modern Creators',
          excerpt: 'How independent creators, artists, and coaches build sustainable six-figure careers without relying on viral social media algorithms.',
          url: 'https://blog.stella.so'
        },
        {
          tag: 'Monetization',
          readTime: '5 min read',
          title: 'Why Direct-to-Fan Subscriptions Outperform Ad Splits',
          excerpt: 'A comprehensive financial breakdown of predictable monthly recurring subscriptions vs. volatile platform ad revenue.',
          url: 'https://blog.stella.so'
        },
        {
          tag: 'Strategy',
          readTime: '3 min read',
          title: 'Maximizing Earnings with Custom Content Requests',
          excerpt: 'How top creators structure, price, and fulfill personalized fan commissions to unlock high-margin incremental income.',
          url: 'https://blog.stella.so'
        }
      ]
    };
  },
  methods: {
    exploreCreators() {
      this.$router.push('/top-creators');
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
          // fallback
        }
      }
    },
    startCreating() {
      this.$router.push({ name: 'CreateAccountMobile' });
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
          // fallback
        }
      }
    },
    openBlog(url) {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  }
};
</script>

<style scoped>
/* ===== GLOBAL WRAPPER ===== */
.front-section {
  background-color: #111111;
  min-height: 100vh;
}

/* ===== HERO SECTION ===== */
.hero-badge {
  display: inline-block;
  padding: 6px 16px;
  border: 1px solid rgba(229, 59, 185, 0.3);
  border-radius: 20px;
  background: rgba(229, 59, 185, 0.08);
}

.badge-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.5px;
}

.hero-title {
  font-family: 'Poppins', sans-serif;
  font-size: 56px;
  font-weight: 700;
  color: white;
  line-height: 1.15;
  letter-spacing: -1px;
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 36px;
  }
}

.hero-gradient-text {
  background: linear-gradient(90deg, #DE8371, #C56FD1, #2F4FE9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.text-primary-gradient {
  background: linear-gradient(90deg, #F3DA6B, #E53BB9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  line-height: 1.6;
  max-width: 680px;
}

/* ===== STATS STRIP ===== */
.stats-strip {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 32px 24px;
}

.stat-item {
  text-align: center;
  padding: 0 32px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.stat-item:last-child {
  border-right: none;
}

.stat-value {
  font-family: 'Poppins', sans-serif;
  font-size: 34px;
  font-weight: 700;
  background: linear-gradient(90deg, #DE8371, #C56FD1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

@media (max-width: 768px) {
  .stat-item {
    padding: 12px 16px;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    width: 50%;
  }
  .stat-item:last-child,
  .stat-item:nth-child(even) {
    border-right: none;
  }
  .stat-value {
    font-size: 26px;
  }
}

/* ===== SECTION TITLES ===== */
.section-title {
  font-family: 'Poppins', sans-serif;
  font-size: 34px;
  font-weight: 700;
  color: white;
}

.section-subtitle {
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  margin-top: 8px;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}

/* ===== COMPARISON CARDS ===== */
.comparison-card {
  transition: transform 0.3s ease, border-color 0.3s ease;
}

.comparison-card:hover {
  transform: translateY(-4px);
}

.solution-glow {
  border-color: rgba(229, 59, 185, 0.3) !important;
}

.status-pill {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  letter-spacing: 0.5px;
}

.status-pill-warning {
  background: rgba(255, 77, 77, 0.12);
  color: #ff6b6b;
  border: 1px solid rgba(255, 77, 77, 0.25);
}

.status-pill-success {
  background: rgba(112, 213, 194, 0.12);
  color: #70D5C2;
  border: 1px solid rgba(112, 213, 194, 0.25);
}

.styled-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.styled-list li {
  font-family: 'Poppins', sans-serif;
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.75);
  padding: 10px 0 10px 28px;
  position: relative;
  line-height: 1.5;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.styled-list li:last-child {
  border-bottom: none;
}

.list-problem li::before {
  content: '✕';
  position: absolute;
  left: 0;
  top: 10px;
  color: #ff6b6b;
  font-weight: 700;
  font-size: 14px;
}

.list-solution li::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 10px;
  color: #70D5C2;
  font-weight: 700;
  font-size: 14px;
}

/* ===== FEATURE PILLAR CARDS ===== */
.feature-pillar-card {
  transition: transform 0.3s ease, border-color 0.3s ease;
  border-radius: 20px !important;
}

.feature-pillar-card:hover {
  transform: translateY(-5px);
  border-color: rgba(229, 59, 185, 0.4) !important;
}

.feature-pillar-icon {
  font-size: 34px;
}

.feature-pillar-highlight {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #F3DA6B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ===== MANIFESTO CARD ===== */
.manifesto-card {
  border-radius: 24px !important;
  background: linear-gradient(135deg, rgba(229, 59, 185, 0.08) 0%, rgba(47, 79, 233, 0.08) 100%) !important;
  border: 1px solid rgba(229, 59, 185, 0.25) !important;
}

.manifesto-quote-mark {
  font-size: 48px;
  line-height: 1;
  color: #E53BB9;
  font-family: 'Space Grotesk', sans-serif;
}

.manifesto-text {
  font-size: 24px;
  font-weight: 600;
  color: white;
  line-height: 1.4;
  max-width: 800px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .manifesto-text {
    font-size: 18px;
  }
}

.manifesto-author {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* ===== PRINCIPLES ===== */
.principle-number {
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.15);
}

/* ===== ARTICLE CARDS ===== */
.article-card {
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  border-radius: 20px !important;
}

.article-card:hover {
  transform: translateY(-4px);
  border-color: rgba(229, 59, 185, 0.35) !important;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4) !important;
}

.article-tag {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
  padding: 3px 10px;
  border-radius: 12px;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ===== CTA CARD ===== */
.cta-card {
  border-radius: 24px !important;
  background: linear-gradient(135deg, rgba(20, 20, 24, 0.9) 0%, rgba(35, 20, 35, 0.9) 100%) !important;
  border: 1px solid rgba(229, 59, 185, 0.3) !important;
}

.cta-title {
  font-family: 'Poppins', sans-serif;
  font-size: 34px;
  font-weight: 700;
  color: white;
}

@media (max-width: 768px) {
  .cta-title {
    font-size: 24px;
  }
}

.cta-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  line-height: 1.6;
  max-width: 600px;
}
</style>
